import { markRaw, type Component } from 'vue'
import { TokenDiamondIcon } from '@uiKit/icons'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import type { TransactionStatus, TransactionType } from '../types/transactionTypes'

export interface TransactionTypeVisual {
  icon?: PiIconName
  /** Glyph colour. Paired with `bg` to make the tinted disc. */
  color?: string
  /** Rendered in place of `icon` when the leg needs a kit icon component. */
  iconComponent?: Component
  bg: string
}

/**
 * Tints mirror the design's four legs: money in (green), invested (violet),
 * rent received (blue), money out (red). Shared by the activity row and the
 * detail hero so the two can't drift apart.
 *
 * These are PiIcon glyphs rather than the SVGs in `@assets/images` — those
 * files bake in their own washed-out fills (`withdrawal.svg` is `#FDA2…`), so
 * they can't take a semantic colour and don't adapt to dark mode.
 */
export const TRANSACTION_TYPE_VISUAL: Record<TransactionType, TransactionTypeVisual> = {
  deposit: { icon: 'arrow-down', color: 'var(--p-success-600)', bg: 'var(--p-success-50)' },
  refund: { icon: 'arrow-down', color: 'var(--p-success-600)', bg: 'var(--p-success-50)' },
  investment: { icon: 'building', color: 'var(--p-primary-600)', bg: 'var(--p-primary-50)' },
  rental_income: { icon: 'home', color: 'var(--p-info-600)', bg: 'var(--p-info-50)' },
  rental: { icon: 'home', color: 'var(--p-info-600)', bg: 'var(--p-info-50)' },
  withdrawal: { icon: 'arrow-up', color: 'var(--p-danger-600)', bg: 'var(--p-danger-50)' },
  token_received: { iconComponent: markRaw(TokenDiamondIcon), bg: 'var(--p-surface-100)' },
}

export const TRANSACTION_STATUS_LABEL: Record<TransactionStatus, string> = {
  completed: 'Completed',
  pending: 'Pending',
  processing: 'Processing',
  failed: 'Failed',
  reversed: 'Reversed',
}

// Settled (green), failed (red) and processing (amber — actively moving) get
// colour. Pending and reversed stay muted so the list reads calm.
export const TRANSACTION_STATUS_COLOR: Record<TransactionStatus, string> = {
  completed: 'var(--p-success-700)',
  pending: 'var(--p-text-muted-color)',
  processing: 'var(--p-warning-600)',
  failed: 'var(--p-danger-600)',
  reversed: 'var(--p-text-muted-color)',
}

export const getTransactionVisual = (type: TransactionType): TransactionTypeVisual =>
  TRANSACTION_TYPE_VISUAL[type] ?? TRANSACTION_TYPE_VISUAL.token_received

/**
 * Day-first activity date ("16 Mar 2026") as the wallet designs write it.
 * `Transaction.date` is the shared en-US display string used by the tabular
 * views, so these screens re-format from the raw timestamp instead.
 */
export function formatActivityDate(iso?: string): string {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}
